import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  FileText, Upload, Download, Search, Filter, Eye,
  Edit, Trash2, Lock, Unlock, Clock, CheckCircle,
  AlertCircle, FolderOpen, Tag, Calendar, User,
  Send, XCircle, ThumbsUp, ThumbsDown, Mail, FileCheck,
  UserCheck, Shield, ChevronDown, ChevronUp, History,
  PenTool, RotateCcw, Save, X, RefreshCw, ClipboardList,
  FileSearch, Pen
} from 'lucide-react';
import axios from '../api/client';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
 
const DocumentControl = () => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [approvalStats, setApprovalStats] = useState(null);
  const [emailLogs, setEmailLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showEmailLogsModal, setShowEmailLogsModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [activeTab, setActiveTab] = useState('all');
  const [approvalComment, setApprovalComment] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [approvalType, setApprovalType] = useState(''); // 'dept' or 'qa'
  const [uploadData, setUploadData] = useState({
    file: null,
    document_name: '',
    document_type: 'SOP',
    category: 'Quality',
    department: '',
    document_category: 'procedures', // BMS folder category
    description: '',
    version: '1.0',
    controlled: true,
    retention_period_years: 7,
    tags: []
  });
 
  // New state for Edit Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [editSaving, setEditSaving] = useState(false);
 
  // New state for Document Preview on Hover
  const [hoveredDocument, setHoveredDocument] = useState(null);
  const [previewPosition, setPreviewPosition] = useState({ x: 0, y: 0 });
  const previewTimeoutRef = useRef(null);
 
  // New state for Digital Signature
  const signatureCanvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureData, setSignatureData] = useState('');
  const [signatureType, setSignatureType] = useState('draw'); // 'draw' or 'type'
  const [typedSignature, setTypedSignature] = useState('');
  const [signatureConfirmed, setSignatureConfirmed] = useState(false);
 
  // New state for ISO Audit Trail
  const [showAuditTrailModal, setShowAuditTrailModal] = useState(false);
  const [auditTrailData, setAuditTrailData] = useState([]);
  const [auditTrailLoading, setAuditTrailLoading] = useState(false);
 
  const documentTypes = ['SOP', 'Policy', 'Procedure', 'Form', 'Record', 'Manual', 'Specification', 'Report', 'Work Instruction'];
  const categories = ['Quality', 'Safety', 'Operations', 'Training', 'Environmental', 'Maintenance', 'Engineering', 'Administration'];
  const statuses = ['Draft', 'Pending Dept Review', 'Pending QA Review', 'Approved', 'Rejected', 'Obsolete', 'Archived'];
  
  // BMS Sub-folder categories
  const bmsCategories = [
    { id: 'procedures', name: 'Procedures' },
    { id: 'lwis', name: "LWI's" },
    { id: 'directive', name: 'Directive' },
    { id: 'forms', name: 'Forms' },
    { id: 'standards', name: 'Standards' }
  ];
  
  const departments = [
    { id: '0100', name: 'Sales and Estimating' },
    { id: '0200', name: 'Project Administration' },
    { id: '0300', name: 'Supply Chain' },
    { id: '0400', name: 'Engineering' },
    { id: '0500', name: 'Manufacturing' },
    { id: '0600', name: 'Quality Control' },
    { id: '0700', name: 'Aftermarket' },
    { id: '0800', name: 'Accounts' },
    { id: '0900', name: 'BMS Administration' },
    { id: '1000', name: 'Human Resources' }
  ];
 
  // Check if user can approve at department level
  const canDeptApprove = user?.role === 'manager' || user?.role === 'admin' || user?.role === 'quality_analyst';
  // Check if user can do final QA approval
  const canQAApprove = user?.role === 'admin' || user?.role === 'quality_analyst' || (user?.role === 'manager' && user?.department?.toLowerCase().includes('quality'));
 
  useEffect(() => {
    fetchDocuments();
    fetchApprovalStats();
  }, []);
 
  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/documents');
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };
 
  const fetchApprovalStats = async () => {
    try {
      const response = await axios.get('/documents/approval-stats');
      setApprovalStats(response.data);
    } catch (error) {
      console.error('Error fetching approval stats:', error);
    }
  };
 
  const fetchEmailLogs = async (documentId) => {
    try {
      const response = await axios.get(`/documents/email-logs?document_id=${documentId}`);
      setEmailLogs(response.data);
    } catch (error) {
      console.error('Error fetching email logs:', error);
      setEmailLogs([]);
    }
  };
 
  const fetchAuditTrail = async (documentId) => {
    setAuditTrailLoading(true);
    try {
      const response = await axios.get(`/documents/${documentId}/audit-trail`);
      setAuditTrailData(response.data);
    } catch (error) {
      console.error('Error fetching audit trail:', error);
      setAuditTrailData([]);
    } finally {
      setAuditTrailLoading(false);
    }
  };
 
  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadData.file) return;
 
    const formData = new FormData();
    formData.append('file', uploadData.file);
    formData.append('document_name', uploadData.document_name);
    formData.append('document_type', uploadData.document_type);
    formData.append('category', uploadData.category);
    formData.append('department', uploadData.department);
    formData.append('document_category', uploadData.document_category);
    formData.append('description', uploadData.description || '');
    formData.append('version', uploadData.version);
    formData.append('controlled', uploadData.controlled);
    formData.append('retention_period_years', uploadData.retention_period_years);
    formData.append('tags', JSON.stringify(uploadData.tags || []));
 
    try {
      // Don't set Content-Type - let browser set it with boundary for multipart
      await axios.post('/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setShowUploadModal(false);
      resetUploadForm();
      fetchDocuments();
      fetchApprovalStats();
      toast.success('Document uploaded successfully! It is now in Draft status.');
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error(error.userMessage || 'Failed to upload document');
    }
  };
 
  const handleDownload = async (docId, docName) => {
    try {
      const response = await axios.get(`/documents/${docId}/download`, {
        responseType: 'blob'
      });
      
      // Check if the response is an error (JSON instead of blob)
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const error = JSON.parse(text);
        alert(error.detail || 'File not available for download');
        return;
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', docName || 'document');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      if (error.response?.status === 404) {
        alert('File not found. The physical file may not be available.');
      } else {
        alert(error.response?.data?.detail || 'Failed to download document');
      }
    }
  };
 
  const handleDelete = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document? This action cannot be undone.')) return;
 
    try {
      await axios.delete(`/documents/${docId}`);
      fetchDocuments();
      fetchApprovalStats();
      alert('Document deleted successfully');
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Failed to delete document');
    }
  };
 
  const handleArchive = async (docId) => {
    if (!window.confirm('Archive this document? It will be moved to the archived section.')) return;
    try {
      await axios.patch(`/documents/${docId}/archive`);
      fetchDocuments();
      fetchApprovalStats();
      alert('Document archived successfully');
    } catch (error) {
      console.error('Error archiving document:', error);
      alert('Failed to archive document');
    }
  };
 
  const handleSubmitForApproval = async (docId) => {
    if (!window.confirm('Submit this document for approval? Email notifications will be sent to the appropriate reviewers.')) return;
 
    try {
      const response = await axios.post(`/documents/${docId}/submit-for-approval`);
      fetchDocuments();
      fetchApprovalStats();
 
      const data = response.data;
      let message = `Document submitted for approval!\n\n`;
      message += `Status: ${data.status}\n`;
      message += `Next Approver: ${data.next_approver === 'qa_manager' ? 'QA Manager' : 'Department Manager'}\n`;
      if (data.email_notification_sent) {
        message += `\nEmail notification sent to reviewer`;
      }
      alert(message);
    } catch (error) {
      console.error('Error submitting for approval:', error);
      alert('Failed to submit for approval');
    }
  };
 
  // ===================== EDIT DOCUMENT HANDLERS =====================
  const handleOpenEdit = (doc) => {
    setEditData({
      id: doc.id,
      document_name: doc.document_name || '',
      document_type: doc.document_type || 'SOP',
      category: doc.category || 'Quality',
      department: doc.department || '',
      description: doc.description || '',
      version: doc.version || '1.0',
      controlled: doc.controlled !== undefined ? doc.controlled : true,
      retention_period_years: doc.retention_period_years || 7,
      tags: doc.tags || []
    });
    setShowEditModal(true);
  };
 
  const handleSaveEdit = async () => {
    if (!editData.id) return;
    setEditSaving(true);
    try {
      const params = new URLSearchParams();
      if (editData.document_name) params.append('document_name', editData.document_name);
      if (editData.document_type) params.append('document_type', editData.document_type);
      if (editData.category) params.append('category', editData.category);
      if (editData.department) params.append('department', editData.department);
      if (editData.description !== undefined) params.append('description', editData.description);
      if (editData.version) params.append('version', editData.version);
      if (editData.controlled !== undefined) params.append('controlled', editData.controlled);
      if (editData.retention_period_years) params.append('retention_period_years', editData.retention_period_years);
      if (editData.tags) params.append('tags', JSON.stringify(editData.tags));
 
      await axios.put(`/documents/${editData.id}?${params.toString()}`);
      setShowEditModal(false);
      setEditData({});
      fetchDocuments();
      fetchApprovalStats();
      alert('Document updated successfully!');
    } catch (error) {
      console.error('Error updating document:', error);
      alert(error.response?.data?.detail || 'Failed to update document');
    } finally {
      setEditSaving(false);
    }
  };
 
  // ===================== HOVER PREVIEW HANDLERS =====================
  const handleMouseEnterDoc = useCallback((doc, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPreviewPosition({
      x: rect.right + 10,
      y: rect.top
    });
    if (previewTimeoutRef.current) clearTimeout(previewTimeoutRef.current);
    previewTimeoutRef.current = setTimeout(() => {
      setHoveredDocument(doc);
    }, 400);
  }, []);
 
  const handleMouseLeaveDoc = useCallback(() => {
    if (previewTimeoutRef.current) clearTimeout(previewTimeoutRef.current);
    setHoveredDocument(null);
  }, []);
 
  const getPreviewUrl = (doc) => {
    if (!doc?.id) return null;
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    return `${baseUrl}/api/documents/${doc.id}/preview`;
  };
 
  const isPreviewableFile = (doc) => {
    const name = (doc?.original_filename || doc?.document_name || '').toLowerCase();
    return name.endsWith('.pdf') || name.endsWith('.png') || name.endsWith('.jpg') ||
           name.endsWith('.jpeg') || name.endsWith('.gif') || name.endsWith('.svg') ||
           name.endsWith('.txt') || name.endsWith('.html');
  };
 
  // ===================== DIGITAL SIGNATURE HANDLERS =====================
  const initCanvas = useCallback(() => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#1e3a5f';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);
 
  useEffect(() => {
    if (showApprovalModal && signatureType === 'draw') {
      setTimeout(initCanvas, 100);
    }
  }, [showApprovalModal, signatureType, initCanvas]);
 
  const getCanvasCoords = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    if (e.touches) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top
      };
    }
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };
 
  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCanvasCoords(e, canvas);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };
 
  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCanvasCoords(e, canvas);
    ctx.lineTo(x, y);
    ctx.stroke();
  };
 
  const stopDrawing = (e) => {
    if (e) e.preventDefault();
    if (!isDrawing) return;
    setIsDrawing(false);
    const canvas = signatureCanvasRef.current;
    if (canvas) {
      setSignatureData(canvas.toDataURL('image/png'));
    }
  };
 
  const clearSignature = () => {
    setSignatureData('');
    setTypedSignature('');
    setSignatureConfirmed(false);
    const canvas = signatureCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
  };
 
  const getSignaturePayload = () => {
    if (signatureType === 'draw') {
      return { signature_data: signatureData, signature_type: 'drawn' };
    }
    return { signature_data: typedSignature, signature_type: 'typed' };
  };
 
  const isSignatureValid = () => {
    if (!signatureConfirmed) return false;
    if (signatureType === 'draw') return !!signatureData;
    return typedSignature.trim().length > 0;
  };
 
  // ===================== APPROVAL HANDLERS WITH SIGNATURES =====================
  const handleDeptApprove = async () => {
    if (!selectedDocument) return;
    if (!isSignatureValid()) {
      alert('Please provide your digital signature and confirm the approval.');
      return;
    }
    try {
      const sigPayload = getSignaturePayload();
      const params = new URLSearchParams();
      params.append('comments', approvalComment);
      params.append('signature_data', sigPayload.signature_data);
      params.append('signature_type', sigPayload.signature_type);
 
      const response = await axios.post(`/documents/${selectedDocument.id}/dept-approve?${params.toString()}`);
      closeApprovalModal();
      fetchDocuments();
      fetchApprovalStats();
      alert(`Document approved with digital signature!\n\n${response.data.message}\nNext: ${response.data.next_step}\nTimestamp: ${response.data.approval_timestamp}`);
    } catch (error) {
      console.error('Error approving document:', error);
      alert('Failed to approve document');
    }
  };
 
  const handleQAApprove = async () => {
    if (!selectedDocument) return;
    if (!isSignatureValid()) {
      alert('Please provide your digital signature and confirm the approval.');
      return;
    }
    try {
      const sigPayload = getSignaturePayload();
      const params = new URLSearchParams();
      params.append('comments', approvalComment);
      params.append('signature_data', sigPayload.signature_data);
      params.append('signature_type', sigPayload.signature_type);
 
      const response = await axios.post(`/documents/${selectedDocument.id}/approve?${params.toString()}`);
      closeApprovalModal();
      fetchDocuments();
      fetchApprovalStats();
 
      const signatures = response.data.signatures;
      let message = `Document OFFICIALLY APPROVED with digital signature!\n\n`;
      message += `Effective Date: ${new Date(response.data.effective_date).toLocaleDateString()}\n\n`;
      message += `SIGNATURES:\n`;
      if (signatures.process_owner) message += `Process Owner: ${signatures.process_owner}\n`;
      if (signatures.dept_manager) message += `Dept Manager: ${signatures.dept_manager}\n`;
      message += `QA Manager: ${signatures.qa_manager}`;
      alert(message);
    } catch (error) {
      console.error('Error approving document:', error);
      alert('Failed to approve document');
    }
  };
 
  const closeApprovalModal = () => {
    setShowApprovalModal(false);
    setApprovalComment('');
    setApprovalType('');
    setSignatureData('');
    setTypedSignature('');
    setSignatureType('draw');
    setSignatureConfirmed(false);
    setSelectedDocument(null);
  };
 
  const handleReject = async () => {
    if (!selectedDocument) return;
    if (!rejectionReason.trim()) {
      alert('Please provide a detailed reason for rejection. This will be sent to the submitter.');
      return;
    }
    try {
      await axios.post(`/documents/${selectedDocument.id}/reject`, null, {
        params: { reason: rejectionReason }
      });
      setShowRejectModal(false);
      setRejectionReason('');
      setSelectedDocument(null);
      fetchDocuments();
      fetchApprovalStats();
      alert('Document rejected. Email notification with rejection reason has been sent to the submitter.');
    } catch (error) {
      console.error('Error rejecting document:', error);
      alert(error.response?.data?.detail || 'Failed to reject document');
    }
  };
 
  const resetUploadForm = () => {
    setUploadData({
      file: null,
      document_name: '',
      document_type: 'SOP',
      category: 'Quality',
      department: '',
      document_category: 'procedures', // BMS folder category
      description: '',
      version: '1.0',
      controlled: true,
      retention_period_years: 7,
      tags: []
    });
  };
 
  const getStatusColor = (status) => {
    switch(status?.toLowerCase()) {
      case 'approved':
      case 'active': return 'bg-green-100 text-green-800 border-green-200';
      case 'pending qa review': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'pending dept review':
      case 'under review': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'draft': return 'bg-slate-100 text-slate-800 border-slate-200';
      case 'rejected': return 'bg-red-100 text-red-800 border-red-200';
      case 'obsolete': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'archived': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };
 
  const getStatusIcon = (status) => {
    switch(status?.toLowerCase()) {
      case 'approved':
      case 'active': return <CheckCircle size={14} className="text-green-600" />;
      case 'pending qa review': return <Shield size={14} className="text-purple-600" />;
      case 'pending dept review':
      case 'under review': return <Clock size={14} className="text-yellow-600" />;
      case 'draft': return <Edit size={14} className="text-slate-600" />;
      case 'rejected': return <XCircle size={14} className="text-red-600" />;
      case 'obsolete': return <AlertCircle size={14} className="text-orange-600" />;
      case 'archived': return <Lock size={14} className="text-blue-600" />;
      default: return <FileText size={14} />;
    }
  };
 
  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.document_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || doc.document_type === filterType;
 
    let matchesStatus = true;
    if (filterStatus !== 'all') {
      matchesStatus = doc.status?.toLowerCase() === filterStatus.toLowerCase();
    }
 
    // Tab filter
    if (activeTab === 'pending-dept') {
      return matchesSearch && matchesType &&
        (doc.approval_status === 'pending_dept_review' || doc.status === 'Pending Dept Review');
    } else if (activeTab === 'pending-qa') {
      return matchesSearch && matchesType &&
        (doc.approval_status === 'pending_qa_review' || doc.status === 'Pending QA Review');
    } else if (activeTab === 'approved') {
      return matchesSearch && matchesType && ['approved', 'active'].includes(doc.status?.toLowerCase());
    } else if (activeTab === 'rejected') {
      return matchesSearch && matchesType && doc.status?.toLowerCase() === 'rejected';
    } else if (activeTab === 'draft') {
      return matchesSearch && matchesType && doc.status?.toLowerCase() === 'draft';
    }
 
    return matchesSearch && matchesType && matchesStatus;
  });
 
  const stats = {
    total: documents.length,
    draft: documents.filter(d => d.status === 'Draft').length,
    pendingDept: documents.filter(d => d.approval_status === 'pending_dept_review' || d.status === 'Pending Dept Review').length,
    pendingQA: documents.filter(d => d.approval_status === 'pending_qa_review' || d.status === 'Pending QA Review').length,
    approved: documents.filter(d => ['Approved', 'active'].includes(d.status)).length,
    rejected: documents.filter(d => d.status === 'Rejected').length,
  };
 
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
 
  return (
    <div data-testid="document-control-page" className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900 flex items-center">
          <FolderOpen className="mr-3 text-blue-600" size={32} />
          Document Control & Approval System
        </h1>
        <p className="text-slate-600 mt-2">
          Multi-level approval workflow with digital signatures & ISO audit trail
        </p>
      </div>
 
      {/* Statistics Cards - NOW CLICKABLE */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
        <button
          onClick={() => setActiveTab('all')}
          className={`bg-white p-4 rounded-lg shadow border text-left transition-all hover:shadow-md hover:scale-[1.02] cursor-pointer ${activeTab === 'all' ? 'border-blue-500 ring-2 ring-blue-200' : 'border-slate-200'}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-500 text-xs uppercase tracking-wide">Total</p>
              <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
            </div>
            <FileText className="text-blue-500" size={24} />
          </div>
        </button>
        <button
          onClick={() => setActiveTab('draft')}
          className={`bg-white p-4 rounded-lg shadow border text-left transition-all hover:shadow-md hover:scale-[1.02] cursor-pointer ${activeTab === 'draft' ? 'border-slate-500 ring-2 ring-slate-200' : 'border-slate-200'}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-500 text-xs uppercase tracking-wide">Draft</p>
              <p className="text-2xl font-bold text-slate-600">{stats.draft}</p>
            </div>
            <Edit className="text-slate-400" size={24} />
          </div>
        </button>
        <button
          onClick={() => setActiveTab('pending-dept')}
          className={`bg-white p-4 rounded-lg shadow border text-left transition-all hover:shadow-md hover:scale-[1.02] cursor-pointer ${activeTab === 'pending-dept' ? 'border-yellow-500 ring-2 ring-yellow-200' : 'border-yellow-200'}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-500 text-xs uppercase tracking-wide">Dept Review</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.pendingDept}</p>
            </div>
            <UserCheck className="text-yellow-500" size={24} />
          </div>
        </button>
        <button
          onClick={() => setActiveTab('pending-qa')}
          className={`bg-white p-4 rounded-lg shadow border text-left transition-all hover:shadow-md hover:scale-[1.02] cursor-pointer ${activeTab === 'pending-qa' ? 'border-purple-500 ring-2 ring-purple-200' : 'border-purple-200'}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-500 text-xs uppercase tracking-wide">QA Review</p>
              <p className="text-2xl font-bold text-purple-600">{stats.pendingQA}</p>
            </div>
            <Shield className="text-purple-500" size={24} />
          </div>
        </button>
        <button
          onClick={() => setActiveTab('approved')}
          className={`bg-white p-4 rounded-lg shadow border text-left transition-all hover:shadow-md hover:scale-[1.02] cursor-pointer ${activeTab === 'approved' ? 'border-green-500 ring-2 ring-green-200' : 'border-green-200'}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-500 text-xs uppercase tracking-wide">Approved</p>
              <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
            </div>
            <CheckCircle className="text-green-500" size={24} />
          </div>
        </button>
        <button
          onClick={() => setActiveTab('rejected')}
          className={`bg-white p-4 rounded-lg shadow border text-left transition-all hover:shadow-md hover:scale-[1.02] cursor-pointer ${activeTab === 'rejected' ? 'border-red-500 ring-2 ring-red-200' : 'border-red-200'}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-500 text-xs uppercase tracking-wide">Rejected</p>
              <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
            </div>
            <XCircle className="text-red-500" size={24} />
          </div>
        </button>
      </div>
 
      {/* Approval Workflow Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h3 className="font-semibold text-blue-900 flex items-center mb-2">
          <Mail className="mr-2" size={18} />
          Approval Workflow with Digital Signatures & ISO Audit Trail
        </h3>
        <div className="flex flex-wrap items-center gap-2 text-sm text-blue-800">
          <span className="bg-white px-3 py-1 rounded-full border border-blue-200">
            1. Draft
          </span>
          <ChevronDown className="rotate-[-90deg]" size={16} />
          <span className="bg-yellow-100 px-3 py-1 rounded-full border border-yellow-300">
            2. Dept Manager Review
          </span>
          <ChevronDown className="rotate-[-90deg]" size={16} />
          <span className="bg-purple-100 px-3 py-1 rounded-full border border-purple-300">
            3. QA Manager Final
          </span>
          <ChevronDown className="rotate-[-90deg]" size={16} />
          <span className="bg-green-100 px-3 py-1 rounded-full border border-green-300">
            4. Approved
          </span>
        </div>
        <p className="text-xs text-blue-700 mt-2">
          * All documents require Department Manager approval before QA Manager review. Digital signatures and full audit trail recorded at each step for ISO compliance.
        </p>
      </div>
 
      {/* Tabs */}
      <div className="bg-white border-b border-slate-200 rounded-t-lg">
        <nav className="flex gap-1 px-2 overflow-x-auto">
          {[
            { id: 'all', label: 'All Documents', count: stats.total },
            { id: 'draft', label: 'Draft', count: stats.draft },
            { id: 'pending-dept', label: 'Pending Dept', count: stats.pendingDept, color: 'yellow' },
            { id: 'pending-qa', label: 'Pending QA', count: stats.pendingQA, color: 'purple' },
            { id: 'approved', label: 'Approved', count: stats.approved, color: 'green' },
            { id: 'rejected', label: 'Rejected', count: stats.rejected, color: 'red' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-3 px-4 border-b-2 transition-colors whitespace-nowrap text-sm ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600 font-medium'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  tab.color === 'yellow' ? 'bg-yellow-100 text-yellow-700' :
                  tab.color === 'purple' ? 'bg-purple-100 text-purple-700' :
                  tab.color === 'green' ? 'bg-green-100 text-green-700' :
                  tab.color === 'red' ? 'bg-red-100 text-red-700' :
                  'bg-slate-100 text-slate-700'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>
 
      {/* Filters and Actions - BUTTONS NOW ACTIVE */}
      <div className="bg-white p-4 shadow mb-6 border border-t-0 border-slate-200 rounded-b-lg">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text"
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm w-64"
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="all">All Types</option>
              {documentTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { fetchDocuments(); fetchApprovalStats(); }}
              className="flex items-center px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors text-sm font-medium border border-slate-300"
              title="Refresh Documents"
            >
              <RefreshCw size={18} className="mr-2" />
              Refresh
            </button>
            <button
              onClick={() => {
                setShowAuditTrailModal(true);
                setSelectedDocument(null);
                setAuditTrailData([]);
              }}
              className="flex items-center px-4 py-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors text-sm font-medium border border-indigo-300"
              title="View Audit Trail"
            >
              <ClipboardList size={18} className="mr-2" />
              Audit Trail
            </button>
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              data-testid="upload-document-btn"
            >
              <Upload size={18} className="mr-2" />
              Upload Document
            </button>
          </div>
        </div>
      </div>
 
      {/* Documents Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden border border-slate-200">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Document</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Department</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Version</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Submitter</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredDocuments.map((doc, index) => (
                <tr key={doc.id || `doc-${index}`} className="hover:bg-slate-50">
                  {/* Document Name Cell with Hover Preview */}
                  <td className="px-4 py-4">
                    <div
                      className="flex items-center cursor-pointer group relative"
                      onMouseEnter={(e) => handleMouseEnterDoc(doc, e)}
                      onMouseLeave={handleMouseLeaveDoc}
                    >
                      <FileText className="text-blue-500 mr-3 flex-shrink-0" size={20} />
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900 truncate max-w-xs group-hover:text-blue-600 transition-colors">
                          {doc.document_name}
                          <FileSearch size={12} className="inline ml-1 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </p>
                        {doc.description && (
                          <p className="text-xs text-slate-500 truncate max-w-xs">{doc.description}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm text-slate-700">{doc.document_type}</td>
                  <td className="px-4 py-4 text-sm text-slate-700">{doc.department || 'N/A'}</td>
                  <td className="px-4 py-4 text-sm text-slate-700">v{doc.version}</td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(doc.status)}`}>
                      {getStatusIcon(doc.status)}
                      <span className="ml-1">{doc.status}</span>
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm text-slate-700">
                    {doc.submitter_name || doc.uploaded_by || 'N/A'}
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-1">
                      {/* View Details */}
                      <button
                        onClick={() => { setSelectedDocument(doc); setShowDetailsModal(true); }}
                        className="text-blue-600 hover:text-blue-800 p-1.5 hover:bg-blue-50 rounded"
                        title="View Details"
                      >
                        <Eye size={16} />
                      </button>
 
                      {/* Edit Document (Draft or Rejected only) */}
                      {(doc.status === 'Draft' || doc.status === 'Rejected') && (
                        <button
                          onClick={() => handleOpenEdit(doc)}
                          className="text-amber-600 hover:text-amber-800 p-1.5 hover:bg-amber-50 rounded"
                          title="Edit Document"
                        >
                          <Pen size={16} />
                        </button>
                      )}
 
                      {/* Download */}
                      {doc.id && (
                        <button
                          onClick={() => handleDownload(doc.id, doc.document_name)}
                          className="text-green-600 hover:text-green-800 p-1.5 hover:bg-green-50 rounded"
                          title="Download"
                        >
                          <Download size={16} />
                        </button>
                      )}
 
                      {/* Submit for Approval (Draft only) */}
                      {doc.status === 'Draft' && (
                        <button
                          onClick={() => handleSubmitForApproval(doc.id)}
                          className="text-yellow-600 hover:text-yellow-800 p-1.5 hover:bg-yellow-50 rounded"
                          title="Submit for Approval"
                        >
                          <Send size={16} />
                        </button>
                      )}
 
                      {/* Department Manager Approval */}
                      {(doc.approval_status === 'pending_dept_review' || doc.status === 'Pending Dept Review') && canDeptApprove && (
                        <button
                          onClick={() => {
                            setSelectedDocument(doc);
                            setApprovalType('dept');
                            setShowApprovalModal(true);
                          }}
                          className="text-yellow-600 hover:text-yellow-800 p-1.5 hover:bg-yellow-50 rounded"
                          title="Department Approval"
                        >
                          <UserCheck size={16} />
                        </button>
                      )}
 
                      {/* QA Manager Final Approval */}
                      {(doc.approval_status === 'pending_qa_review' || doc.status === 'Pending QA Review') && canQAApprove && (
                        <button
                          onClick={() => {
                            setSelectedDocument(doc);
                            setApprovalType('qa');
                            setShowApprovalModal(true);
                          }}
                          className="text-purple-600 hover:text-purple-800 p-1.5 hover:bg-purple-50 rounded"
                          title="QA Final Approval"
                        >
                          <Shield size={16} />
                        </button>
                      )}
 
                      {/* Reject (for reviewers) */}
                      {(['pending_dept_review', 'pending_qa_review'].includes(doc.approval_status) ||
                        ['Pending Dept Review', 'Pending QA Review'].includes(doc.status)) &&
                        (canDeptApprove || canQAApprove) && (
                        <button
                          onClick={() => { setSelectedDocument(doc); setShowRejectModal(true); }}
                          className="text-red-600 hover:text-red-800 p-1.5 hover:bg-red-50 rounded"
                          title="Reject"
                        >
                          <ThumbsDown size={16} />
                        </button>
                      )}
 
                      {/* Audit Trail */}
                      <button
                        onClick={() => {
                          setSelectedDocument(doc);
                          fetchAuditTrail(doc.id);
                          setShowAuditTrailModal(true);
                        }}
                        className="text-indigo-600 hover:text-indigo-800 p-1.5 hover:bg-indigo-50 rounded"
                        title="ISO Audit Trail"
                      >
                        <ClipboardList size={16} />
                      </button>
 
                      {/* Email Logs */}
                      <button
                        onClick={() => {
                          setSelectedDocument(doc);
                          fetchEmailLogs(doc.id);
                          setShowEmailLogsModal(true);
                        }}
                        className="text-slate-600 hover:text-slate-800 p-1.5 hover:bg-slate-50 rounded"
                        title="Email History"
                      >
                        <Mail size={16} />
                      </button>
 
                      {/* Archive */}
                      <button
                        onClick={() => handleArchive(doc.id)}
                        className="text-slate-500 hover:text-slate-700 p-1.5 hover:bg-slate-50 rounded"
                        title="Archive"
                      >
                        <Lock size={16} />
                      </button>
 
                      {/* Delete */}
                      <button
                        onClick={() => handleDelete(doc.id)}
                        className="text-red-500 hover:text-red-700 p-1.5 hover:bg-red-50 rounded"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredDocuments.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <FileText className="mx-auto mb-4 text-slate-300" size={48} />
            <p className="font-medium">No documents found</p>
            <p className="text-sm mt-1">Try adjusting your filters or upload a new document</p>
          </div>
        )}
      </div>
 
      {/* ==================== HOVER DOCUMENT PREVIEW ==================== */}
      {hoveredDocument && (
        <div
          className="fixed z-[100] bg-white rounded-lg shadow-2xl border border-slate-300 overflow-hidden"
          style={{
            left: Math.min(previewPosition.x, window.innerWidth - 420),
            top: Math.max(10, Math.min(previewPosition.y, window.innerHeight - 360)),
            width: 400,
            maxHeight: 340,
          }}
          onMouseEnter={() => { if (previewTimeoutRef.current) clearTimeout(previewTimeoutRef.current); }}
          onMouseLeave={handleMouseLeaveDoc}
        >
          <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-800 truncate">
              {hoveredDocument.document_name}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(hoveredDocument.status)}`}>
              {hoveredDocument.status}
            </span>
          </div>
          {isPreviewableFile(hoveredDocument) ? (
            <div className="w-full h-64 bg-slate-100">
              <iframe
                src={getPreviewUrl(hoveredDocument)}
                title="Document Preview"
                className="w-full h-full border-0"
                sandbox="allow-same-origin"
              />
            </div>
          ) : (
            <div className="p-4 space-y-2">
              <div className="flex items-center mb-3">
                <FileText className="text-blue-500 mr-2" size={32} />
                <div>
                  <p className="font-medium text-slate-900">{hoveredDocument.document_name}</p>
                  <p className="text-xs text-slate-500">{hoveredDocument.original_filename || 'No file name'}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-slate-500">Type:</span> <span className="font-medium">{hoveredDocument.document_type}</span></div>
                <div><span className="text-slate-500">Version:</span> <span className="font-medium">v{hoveredDocument.version}</span></div>
                <div><span className="text-slate-500">Category:</span> <span className="font-medium">{hoveredDocument.category || 'N/A'}</span></div>
                <div><span className="text-slate-500">Dept:</span> <span className="font-medium">{hoveredDocument.department || 'N/A'}</span></div>
                <div><span className="text-slate-500">Controlled:</span> <span className="font-medium">{hoveredDocument.controlled ? 'Yes' : 'No'}</span></div>
                <div><span className="text-slate-500">Retention:</span> <span className="font-medium">{hoveredDocument.retention_period_years || 7} yrs</span></div>
              </div>
              {hoveredDocument.description && (
                <div className="mt-2 pt-2 border-t border-slate-200">
                  <p className="text-xs text-slate-500">Description:</p>
                  <p className="text-xs text-slate-700 mt-0.5">{hoveredDocument.description}</p>
                </div>
              )}
              {hoveredDocument.upload_date && (
                <p className="text-xs text-slate-400 mt-1">
                  Uploaded: {new Date(hoveredDocument.upload_date).toLocaleDateString()}
                </p>
              )}
              <div className="mt-2 pt-2 border-t border-slate-200 text-xs text-slate-400">
                Hover to preview - Click Download button to view full document
              </div>
            </div>
          )}
        </div>
      )}
 
      {/* ==================== EDIT DOCUMENT MODAL ==================== */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4 text-slate-900 flex items-center">
              <Pen className="mr-2 text-amber-600" size={24} />
              Edit Document
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">Document Name *</label>
                <input
                  type="text"
                  value={editData.document_name}
                  onChange={(e) => setEditData({...editData, document_name: e.target.value})}
                  placeholder="Enter document name"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Document Type *</label>
                <select
                  value={editData.document_type}
                  onChange={(e) => setEditData({...editData, document_type: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                >
                  {documentTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Category *</label>
                <select
                  value={editData.category}
                  onChange={(e) => setEditData({...editData, category: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Department *</label>
                <select
                  value={editData.department}
                  onChange={(e) => setEditData({...editData, department: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                >
                  <option value="">Select Department</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.name}>{dept.id} - {dept.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Version</label>
                <input
                  type="text"
                  value={editData.version}
                  onChange={(e) => setEditData({...editData, version: e.target.value})}
                  placeholder="1.0"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Retention Period (Years)</label>
                <input
                  type="number"
                  value={editData.retention_period_years}
                  onChange={(e) => setEditData({...editData, retention_period_years: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div className="flex items-center">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editData.controlled}
                    onChange={(e) => setEditData({...editData, controlled: e.target.checked})}
                    className="mr-2 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                  />
                  <span className="text-sm font-medium text-slate-700">Controlled Document</span>
                </label>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <textarea
                  value={editData.description}
                  onChange={(e) => setEditData({...editData, description: e.target.value})}
                  rows="3"
                  placeholder="Enter document description..."
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => { setShowEditModal(false); setEditData({}); }}
                className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={editSaving}
                className="flex items-center px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
              >
                <Save size={16} className="mr-2" />
                {editSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
 
      {/* ==================== UPLOAD MODAL ==================== */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4 text-slate-900 flex items-center">
              <Upload className="mr-2 text-blue-600" size={24} />
              Upload New Document
            </h2>
            <form onSubmit={handleUpload}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Select File *</label>
                  <input
                    type="file"
                    required
                    onChange={(e) => setUploadData({...uploadData, file: e.target.files[0]})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Document Name *</label>
                  <input
                    type="text"
                    required
                    value={uploadData.document_name}
                    onChange={(e) => setUploadData({...uploadData, document_name: e.target.value})}
                    placeholder="Enter document name"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Document Type *</label>
                  <select
                    required
                    value={uploadData.document_type}
                    onChange={(e) => setUploadData({...uploadData, document_type: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {documentTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Category *</label>
                  <select
                    required
                    value={uploadData.category}
                    onChange={(e) => setUploadData({...uploadData, category: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Department *</label>
                  <select
                    required
                    value={uploadData.department}
                    onChange={(e) => setUploadData({...uploadData, department: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Department</option>
                    {departments.map(dept => (
                      <option key={dept.id} value={dept.name}>{dept.id} - {dept.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">BMS Folder Category *</label>
                  <select
                    required
                    value={uploadData.document_category}
                    onChange={(e) => setUploadData({...uploadData, document_category: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {bmsCategories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-500 mt-1">Determines folder location in BMS Administration after approval</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Version</label>
                  <input
                    type="text"
                    value={uploadData.version}
                    onChange={(e) => setUploadData({...uploadData, version: e.target.value})}
                    placeholder="1.0"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Retention Period (Years)</label>
                  <input
                    type="number"
                    value={uploadData.retention_period_years}
                    onChange={(e) => setUploadData({...uploadData, retention_period_years: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex items-center">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={uploadData.controlled}
                      onChange={(e) => setUploadData({...uploadData, controlled: e.target.checked})}
                      className="mr-2 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-slate-700">Controlled Document</span>
                  </label>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <textarea
                    value={uploadData.description}
                    onChange={(e) => setUploadData({...uploadData, description: e.target.value})}
                    rows="3"
                    placeholder="Enter document description..."
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => { setShowUploadModal(false); resetUploadForm(); }}
                  className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Upload Document
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
 
      {/* ==================== DETAILS MODAL ==================== */}
      {showDetailsModal && selectedDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4 text-slate-900 flex items-center">
              <FileCheck className="mr-2 text-blue-600" size={24} />
              Document Details
            </h2>
 
            <div className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-lg">
                <div><span className="text-slate-500 text-sm">Name:</span> <span className="font-medium text-slate-900 block">{selectedDocument.document_name}</span></div>
                <div><span className="text-slate-500 text-sm">Type:</span> <span className="font-medium text-slate-900 block">{selectedDocument.document_type}</span></div>
                <div><span className="text-slate-500 text-sm">Category:</span> <span className="font-medium text-slate-900 block">{selectedDocument.category || 'N/A'}</span></div>
                <div><span className="text-slate-500 text-sm">Department:</span> <span className="font-medium text-slate-900 block">{selectedDocument.department || 'N/A'}</span></div>
                <div><span className="text-slate-500 text-sm">Version:</span> <span className="font-medium text-slate-900 block">v{selectedDocument.version}</span></div>
                <div><span className="text-slate-500 text-sm">Status:</span> <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(selectedDocument.status)}`}>{selectedDocument.status}</span></div>
              </div>
 
              {/* Description */}
              {selectedDocument.description && (
                <div className="bg-slate-50 p-4 rounded-lg">
                  <span className="text-slate-500 text-sm">Description:</span>
                  <p className="text-slate-900 mt-1">{selectedDocument.description}</p>
                </div>
              )}
 
              {/* Approval Chain with Signature Data */}
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="font-semibold text-blue-900 mb-3 flex items-center">
                  <History className="mr-2" size={18} />
                  Approval Chain & Digital Signatures
                </h3>
                <div className="space-y-3">
                  {/* Process Owner */}
                  <div className="flex items-start">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${selectedDocument.process_owner_name ? 'bg-green-500' : 'bg-slate-300'}`}>
                      {selectedDocument.process_owner_name ? <CheckCircle size={16} className="text-white" /> : <User size={16} className="text-white" />}
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-slate-900">Process Owner (Auto-Signed)</p>
                      {selectedDocument.process_owner_name ? (
                        <p className="text-xs text-slate-600">{selectedDocument.process_owner_name} &bull; {selectedDocument.process_owner_signature_date ? new Date(selectedDocument.process_owner_signature_date).toLocaleString() : 'Pending'}</p>
                      ) : (
                        <p className="text-xs text-slate-500">Pending submission</p>
                      )}
                    </div>
                  </div>
 
                  {/* Department Manager */}
                  {selectedDocument.requires_dept_approval !== false && (
                    <div className="flex items-start">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${selectedDocument.dept_approver_name ? 'bg-green-500' : selectedDocument.approval_status === 'pending_dept_review' ? 'bg-yellow-500' : 'bg-slate-300'}`}>
                        {selectedDocument.dept_approver_name ? <CheckCircle size={16} className="text-white" /> : <UserCheck size={16} className="text-white" />}
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-slate-900">Department Manager</p>
                        {selectedDocument.dept_approver_name ? (
                          <>
                            <p className="text-xs text-slate-600">{selectedDocument.dept_approver_name} &bull; {selectedDocument.dept_approval_date ? new Date(selectedDocument.dept_approval_date).toLocaleString() : ''}</p>
                            {selectedDocument.dept_signature_data && selectedDocument.dept_signature_type === 'drawn' && (
                              <img src={selectedDocument.dept_signature_data} alt="Department Manager Signature" className="h-10 mt-1 border border-slate-200 rounded bg-white p-0.5" />
                            )}
                            {selectedDocument.dept_signature_data && selectedDocument.dept_signature_type === 'typed' && (
                              <p className="text-sm italic font-serif text-blue-800 mt-1">~ {selectedDocument.dept_signature_data}</p>
                            )}
                          </>
                        ) : selectedDocument.approval_status === 'pending_dept_review' ? (
                          <p className="text-xs text-yellow-600">Awaiting approval</p>
                        ) : (
                          <p className="text-xs text-slate-500">Pending</p>
                        )}
                        {selectedDocument.dept_approval_comments && (
                          <p className="text-xs text-slate-500 mt-1 italic">"{selectedDocument.dept_approval_comments}"</p>
                        )}
                      </div>
                    </div>
                  )}
 
                  {/* QA Manager */}
                  <div className="flex items-start">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${selectedDocument.qa_approver_name ? 'bg-green-500' : selectedDocument.approval_status === 'pending_qa_review' ? 'bg-purple-500' : 'bg-slate-300'}`}>
                      {selectedDocument.qa_approver_name ? <CheckCircle size={16} className="text-white" /> : <Shield size={16} className="text-white" />}
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-slate-900">QA Manager (Final Approval)</p>
                      {selectedDocument.qa_approver_name ? (
                        <>
                          <p className="text-xs text-slate-600">{selectedDocument.qa_approver_name} &bull; {selectedDocument.qa_approval_date ? new Date(selectedDocument.qa_approval_date).toLocaleString() : ''}</p>
                          {selectedDocument.qa_signature_data && selectedDocument.qa_signature_type === 'drawn' && (
                            <img src={selectedDocument.qa_signature_data} alt="QA Manager Signature" className="h-10 mt-1 border border-slate-200 rounded bg-white p-0.5" />
                          )}
                          {selectedDocument.qa_signature_data && selectedDocument.qa_signature_type === 'typed' && (
                            <p className="text-sm italic font-serif text-blue-800 mt-1">~ {selectedDocument.qa_signature_data}</p>
                          )}
                        </>
                      ) : selectedDocument.approval_status === 'pending_qa_review' ? (
                        <p className="text-xs text-purple-600">Awaiting final approval</p>
                      ) : (
                        <p className="text-xs text-slate-500">Pending</p>
                      )}
                      {selectedDocument.qa_approval_comments && (
                        <p className="text-xs text-slate-500 mt-1 italic">"{selectedDocument.qa_approval_comments}"</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
 
              {/* Rejection Info */}
              {selectedDocument.status === 'Rejected' && (
                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <h3 className="font-semibold text-red-900 mb-2 flex items-center">
                    <XCircle className="mr-2" size={18} />
                    Rejection Details
                  </h3>
                  <p className="text-red-800"><strong>Rejected By:</strong> {selectedDocument.rejector_name || 'Reviewer'}</p>
                  <p className="text-red-800"><strong>Date:</strong> {selectedDocument.rejection_date ? new Date(selectedDocument.rejection_date).toLocaleString() : 'N/A'}</p>
                  <p className="text-red-800 mt-2"><strong>Reason:</strong></p>
                  <p className="text-red-700 bg-white p-3 rounded mt-1 border border-red-200">{selectedDocument.rejection_reason || 'No reason provided'}</p>
                </div>
              )}
 
              {/* Effective Date for Approved */}
              {selectedDocument.status === 'Approved' && selectedDocument.effective_date && (
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <h3 className="font-semibold text-green-900 mb-2 flex items-center">
                    <CheckCircle className="mr-2" size={18} />
                    Document Approved
                  </h3>
                  <p className="text-green-800">Effective Date: {new Date(selectedDocument.effective_date).toLocaleDateString()}</p>
                </div>
              )}
            </div>
 
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  fetchAuditTrail(selectedDocument.id);
                  setShowDetailsModal(false);
                  setShowAuditTrailModal(true);
                }}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center"
              >
                <ClipboardList size={16} className="mr-2" />
                View Audit Trail
              </button>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
 
      {/* ==================== APPROVAL MODAL WITH DIGITAL SIGNATURE ==================== */}
      {showApprovalModal && selectedDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4 text-slate-900 flex items-center">
              {approvalType === 'dept' ? (
                <>
                  <UserCheck className="mr-2 text-yellow-600" size={24} />
                  Department Manager Approval
                </>
              ) : (
                <>
                  <Shield className="mr-2 text-purple-600" size={24} />
                  QA Manager Final Approval
                </>
              )}
            </h2>
 
            {/* Document Info */}
            <div className="bg-slate-50 p-4 rounded-lg mb-4">
              <p className="text-slate-700"><strong>Document:</strong> {selectedDocument.document_name}</p>
              <p className="text-slate-700"><strong>Type:</strong> {selectedDocument.document_type}</p>
              <p className="text-slate-700"><strong>Version:</strong> v{selectedDocument.version}</p>
              <p className="text-slate-700"><strong>Submitted By:</strong> {selectedDocument.submitter_name || 'Unknown'}</p>
            </div>
 
            {approvalType === 'qa' && (
              <div className="bg-purple-50 p-3 rounded-lg mb-4 text-sm border border-purple-200">
                <p className="text-purple-800 font-medium">Final Approval</p>
                <p className="text-purple-700">Your digital signature and approval will make this document official. All signatures are recorded for ISO audit compliance.</p>
              </div>
            )}
 
            {/* Comments */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-1">Comments (Optional)</label>
              <textarea
                value={approvalComment}
                onChange={(e) => setApprovalComment(e.target.value)}
                rows="2"
                placeholder="Enter any comments for the approval..."
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
 
            {/* Digital Signature Section */}
            <div className="border-2 border-dashed border-blue-300 rounded-lg p-4 mb-4 bg-blue-50">
              <h3 className="font-semibold text-blue-900 mb-3 flex items-center">
                <PenTool className="mr-2" size={18} />
                Digital Signature (Required for ISO Compliance)
              </h3>
 
              {/* Signature Type Toggle */}
              <div className="flex gap-2 mb-3">
                <button
                  onClick={() => { setSignatureType('draw'); clearSignature(); }}
                  className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                    signatureType === 'draw'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-slate-600 border border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <PenTool size={14} className="inline mr-1" />
                  Draw Signature
                </button>
                <button
                  onClick={() => { setSignatureType('type'); clearSignature(); }}
                  className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                    signatureType === 'type'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-slate-600 border border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <Edit size={14} className="inline mr-1" />
                  Type Signature
                </button>
              </div>
 
              {/* Draw Signature Canvas */}
              {signatureType === 'draw' && (
                <div className="mb-3">
                  <div className="bg-white border-2 border-slate-300 rounded-lg overflow-hidden">
                    <canvas
                      ref={signatureCanvasRef}
                      width={500}
                      height={150}
                      className="w-full cursor-crosshair touch-none"
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      onTouchStart={startDrawing}
                      onTouchMove={draw}
                      onTouchEnd={stopDrawing}
                    />
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <p className="text-xs text-slate-500">Draw your signature above using mouse or touch</p>
                    <button
                      onClick={clearSignature}
                      className="text-xs text-red-600 hover:text-red-800 flex items-center"
                    >
                      <RotateCcw size={12} className="mr-1" />
                      Clear
                    </button>
                  </div>
                </div>
              )}
 
              {/* Type Signature */}
              {signatureType === 'type' && (
                <div className="mb-3">
                  <input
                    type="text"
                    value={typedSignature}
                    onChange={(e) => setTypedSignature(e.target.value)}
                    placeholder="Type your full legal name..."
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-lg"
                  />
                  {typedSignature && (
                    <div className="mt-2 p-3 bg-white border border-slate-200 rounded-lg">
                      <p className="text-xs text-slate-500 mb-1">Signature Preview:</p>
                      <p className="text-2xl italic font-serif text-blue-900">{typedSignature}</p>
                    </div>
                  )}
                </div>
              )}
 
              {/* Confirmation Checkbox */}
              <div className="bg-white p-3 rounded-lg border border-slate-200">
                <label className="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    checked={signatureConfirmed}
                    onChange={(e) => setSignatureConfirmed(e.target.checked)}
                    className="mt-0.5 mr-3 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-slate-700">
                    <strong>I confirm</strong> that I am {user?.name || 'the authorized approver'}, I have reviewed this document,
                    and I authorize this {approvalType === 'qa' ? 'final ' : ''}approval. I understand this digital signature
                    constitutes a legally binding electronic signature per 21 CFR Part 11 and ISO 9001 requirements.
                  </span>
                </label>
              </div>
 
              {/* Timestamp Info */}
              <div className="mt-2 text-xs text-slate-500 flex items-center">
                <Clock size={12} className="mr-1" />
                Signature timestamp: {new Date().toLocaleString()} | User: {user?.name || user?.email || 'Current User'}
              </div>
            </div>
 
            <div className="flex justify-end space-x-3">
              <button
                onClick={closeApprovalModal}
                className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={approvalType === 'dept' ? handleDeptApprove : handleQAApprove}
                disabled={!isSignatureValid()}
                className={`flex items-center px-4 py-2 text-white rounded-lg transition-colors ${
                  !isSignatureValid()
                    ? 'bg-slate-400 cursor-not-allowed'
                    : approvalType === 'dept'
                      ? 'bg-yellow-600 hover:bg-yellow-700'
                      : 'bg-purple-600 hover:bg-purple-700'
                }`}
              >
                <ThumbsUp size={16} className="mr-2" />
                {approvalType === 'dept' ? 'Sign & Approve (Forward to QA)' : 'Sign & Final Approve'}
              </button>
            </div>
          </div>
        </div>
      )}
 
      {/* ==================== REJECT MODAL ==================== */}
      {showRejectModal && selectedDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full">
            <h2 className="text-2xl font-bold mb-4 text-red-900 flex items-center">
              <XCircle className="mr-2 text-red-600" size={24} />
              Reject Document
            </h2>
 
            <div className="bg-slate-50 p-4 rounded-lg mb-4">
              <p className="text-slate-700"><strong>Document:</strong> {selectedDocument.document_name}</p>
              <p className="text-slate-700"><strong>Submitted By:</strong> {selectedDocument.submitter_name || 'Unknown'}</p>
            </div>
 
            <div className="bg-red-50 p-3 rounded-lg mb-4 text-sm border border-red-200">
              <p className="text-red-800 font-medium">Email Notification</p>
              <p className="text-red-700">Your rejection reason will be sent to the submitter via email with instructions to revise and resubmit.</p>
            </div>
 
            <div className="mb-4">
              <label className="block text-sm font-medium text-red-700 mb-1">
                Reason for Rejection * <span className="text-slate-500 font-normal">(Required - be specific)</span>
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows="5"
                placeholder="Please provide a detailed explanation of why this document is being rejected. Include specific issues that need to be addressed before resubmission."
                className="w-full px-3 py-2 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500"
                required
              />
            </div>
 
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => { setShowRejectModal(false); setRejectionReason(''); }}
                className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                <ThumbsDown size={16} className="mr-2" />
                Reject & Notify
              </button>
            </div>
          </div>
        </div>
      )}
 
      {/* ==================== EMAIL LOGS MODAL ==================== */}
      {showEmailLogsModal && selectedDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4 text-slate-900 flex items-center">
              <Mail className="mr-2 text-blue-600" size={24} />
              Email Notification History
            </h2>
 
            <div className="bg-slate-50 p-3 rounded-lg mb-4">
              <p className="text-slate-700"><strong>Document:</strong> {selectedDocument.document_name}</p>
            </div>
 
            {emailLogs.length > 0 ? (
              <div className="space-y-3">
                {emailLogs.map((log, index) => (
                  <div key={log.id || index} className="border border-slate-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        log.email_type === 'document_approved' ? 'bg-green-100 text-green-800' :
                        log.email_type === 'document_rejected' ? 'bg-red-100 text-red-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {log.email_type?.replace(/_/g, ' ').toUpperCase()}
                      </span>
                      <span className="text-xs text-slate-500">
                        {log.sent_at ? new Date(log.sent_at).toLocaleString() : 'N/A'}
                      </span>
                    </div>
                    <p className="text-sm text-slate-700"><strong>To:</strong> {log.to_name} ({log.to_email})</p>
                    <p className="text-sm text-slate-700"><strong>Subject:</strong> {log.subject}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Mail className="mx-auto mb-2 text-slate-300" size={32} />
                <p>No email notifications yet for this document</p>
              </div>
            )}
 
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => { setShowEmailLogsModal(false); setEmailLogs([]); }}
                className="px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
 
      {/* ==================== ISO AUDIT TRAIL MODAL ==================== */}
      {showAuditTrailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4 text-slate-900 flex items-center">
              <ClipboardList className="mr-2 text-indigo-600" size={24} />
              ISO Audit Trail
              {selectedDocument && (
                <span className="ml-2 text-base font-normal text-slate-500">
                  - {selectedDocument.document_name}
                </span>
              )}
            </h2>
 
            {/* ISO Compliance Info */}
            <div className="bg-indigo-50 p-4 rounded-lg mb-4 border border-indigo-200">
              <h3 className="font-semibold text-indigo-900 mb-1 text-sm">ISO 9001:2015 / 21 CFR Part 11 Compliant Record</h3>
              <p className="text-xs text-indigo-700">
                This audit trail provides a complete, tamper-evident record of all document actions including creation, edits, reviews,
                approvals, rejections, and digital signatures. All entries are timestamped in UTC and associated with authenticated users.
              </p>
            </div>
 
            {/* Select document if not already selected */}
            {!selectedDocument && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-1">Select Document to View Audit Trail</label>
                <select
                  onChange={(e) => {
                    const doc = documents.find(d => d.id === e.target.value);
                    if (doc) {
                      setSelectedDocument(doc);
                      fetchAuditTrail(doc.id);
                    }
                  }}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">-- Select a document --</option>
                  {documents.map(doc => (
                    <option key={doc.id} value={doc.id}>{doc.document_name} (v{doc.version})</option>
                  ))}
                </select>
              </div>
            )}
 
            {auditTrailLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                <span className="ml-3 text-slate-500">Loading audit trail...</span>
              </div>
            ) : auditTrailData.length > 0 ? (
              <div className="space-y-0">
                {/* Table Header */}
                <div className="bg-slate-100 rounded-t-lg border border-slate-200">
                  <div className="grid grid-cols-12 gap-2 px-4 py-2 text-xs font-semibold text-slate-600 uppercase">
                    <div className="col-span-2">Timestamp</div>
                    <div className="col-span-2">Action</div>
                    <div className="col-span-2">User</div>
                    <div className="col-span-6">Details</div>
                  </div>
                </div>
                {/* Table Body */}
                <div className="border-x border-b border-slate-200 rounded-b-lg divide-y divide-slate-100">
                  {auditTrailData.map((entry, index) => (
                    <div key={entry.id || index} className="grid grid-cols-12 gap-2 px-4 py-3 text-sm hover:bg-slate-50">
                      <div className="col-span-2 text-xs text-slate-600">
                        {entry.timestamp ? new Date(entry.timestamp).toLocaleString() : 'N/A'}
                      </div>
                      <div className="col-span-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          entry.action === 'uploaded' ? 'bg-blue-100 text-blue-800' :
                          entry.action === 'edited' ? 'bg-amber-100 text-amber-800' :
                          entry.action === 'submitted_for_approval' ? 'bg-yellow-100 text-yellow-800' :
                          entry.action === 'dept_approved' ? 'bg-yellow-100 text-yellow-800' :
                          entry.action === 'qa_approved' ? 'bg-green-100 text-green-800' :
                          entry.action === 'rejected' ? 'bg-red-100 text-red-800' :
                          entry.action === 'downloaded' ? 'bg-slate-100 text-slate-800' :
                          entry.action === 'previewed' ? 'bg-slate-100 text-slate-800' :
                          entry.action === 'archived' ? 'bg-blue-100 text-blue-800' :
                          entry.action === 'deleted' ? 'bg-red-100 text-red-800' :
                          entry.action === 'status_changed' ? 'bg-purple-100 text-purple-800' :
                          'bg-slate-100 text-slate-800'
                        }`}>
                          {entry.action?.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <div className="col-span-2 text-xs text-slate-700">
                        <p className="font-medium">{entry.employee_name}</p>
                        <p className="text-slate-400">{entry.employee_id?.substring(0, 8)}...</p>
                      </div>
                      <div className="col-span-6 text-xs text-slate-600">
                        {entry.details}
                      </div>
                    </div>
                  ))}
                </div>
 
                {/* Summary Footer */}
                <div className="mt-4 bg-slate-50 p-3 rounded-lg border border-slate-200 text-xs text-slate-500">
                  <div className="flex justify-between">
                    <span>Total entries: {auditTrailData.length}</span>
                    <span>Generated: {new Date().toLocaleString()}</span>
                  </div>
                </div>
              </div>
            ) : selectedDocument ? (
              <div className="text-center py-12 text-slate-500">
                <ClipboardList className="mx-auto mb-4 text-slate-300" size={48} />
                <p className="font-medium">No audit trail entries found</p>
                <p className="text-sm mt-1">Actions on this document will be recorded here</p>
              </div>
            ) : null}
 
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => { setShowAuditTrailModal(false); setAuditTrailData([]); setSelectedDocument(null); }}
                className="px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
 
export default DocumentControl;
